import React, { useState, useEffect, useCallback } from 'react'
import { useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import defaultTexts from '../../../texts/texts'
import constants from '../../../config/constants'
import useWindowDimensions from '../../_common/WindowDimesnions'
import CustomSlickSlider from '../../_common/CustomSlickSlider'
import CustomDialog from '../../_common/CustomDialog'
import ServiceTitleItem from './ServiceTitleItem'
import RegionItem from './RegionItem'
import ServiceAdd from './ServiceAdd'
import ServiceEdit from './ServiceEdit'
import RegionDelete from './RegionDelete'

const OpenStackServicesWrapper = (props) => {
  const { services, billingServices, supportedServiceConfigs, showBillingService, setFetchServices, subscriptionName } = props
  const theme = useTheme()
  const { width } = useWindowDimensions()
  
  let serviceTitleWidth = 200
  let nextArrowWidth = 65

  if (width < constants.xlBreakpoint) {
    serviceTitleWidth = 100
  }

  if (width < constants.mdBreakpoint) {
    nextArrowWidth = 20
  }
  
  const [serviceNames, setServiceNames] = useState([])
  const [regions, setRegions] = useState([])
  const [showAllRegions, setShowAllRegions] = useState(1)
  const [serviceAddDialogOpen, setServiceAddDialogOpen] = useState(false)
  const [serviceEditDialogOpen, setServiceEditDialogOpen] = useState(false)
  const [regionDeleteDialogOpen, setRegionDeleteDialogOpen] = useState(false)
  const [dialogTitle, setDialogTitle] = useState('')
  const [selectedService, setSelectedService] = useState(null)
  const [identityServiceName, setIdentityServiceName] = useState(null)
  
  const [sliderData, setSliderData] = useState([])
  const [sliderSlidesToShow, setsSlidesToShow] = useState(0)
  
  const labelStyle = {
    color: theme.palette.vodka,
    fontSize: '18px',
    fontWeight: '500',
  }

  const labelArrowStyle = {
    color: theme.palette.oxfordBlue,
  }

  const allRegionsButtonWrapperStyle = {
    height: '50px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }

  const allRegionsButtonStyle = {
    color: theme.palette.vodka,
    fontSize: width >= constants.xlBreakpoint ? '18px' : '14px',
    fontWeight: '500',
    textTransform: 'capitalize',

    '&:hover': {
      background: 'inherit',
      color: theme.palette.oxfordBlue,
    },
  }

  const sliderArrows = {
    rightIcon: <ArrowForwardIcon fontSize='large' />,
    rightStyle: {
      width: '50px',
      height: '50px',
      border: `${theme.palette.oxfordBlue} 2px solid`,
      borderRadius: '25px',
      color: theme.palette.oxfordBlue,
      position: 'absolute',
      top: '50%',
      right: '-50px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  }

  const handleShowAllRegions = () => {
    setShowAllRegions(prev => prev + 1)
  }

  const handleServiceAddDialogOpen = (props) => {
    setServiceAddDialogOpen(true)
    setSelectedService(props.service_name)
    setDialogTitle(props.title)
  }

  const handleServiceAddDialogClose = () => {
    setServiceAddDialogOpen(false)
    setSelectedService(null)
    setDialogTitle('')
  }

  const handleServiceEditDialogOpen = (props) => {
    setServiceEditDialogOpen(true)
    setSelectedService(props.service)
    setDialogTitle(props.title)
  }

  const handleServiceEditDialogClose = () => {
    setServiceEditDialogOpen(false)
    setSelectedService(null)
    setDialogTitle('')
  }

  const handleRegionDeleteDialogOpen = (props) => {
    setRegionDeleteDialogOpen(true)
    setSelectedService(props.region_name)
    setDialogTitle(props.title)
  }

  const handleRegionDeleteDialogClose = () => {
    setRegionDeleteDialogOpen(false)
    setSelectedService(null)
    setDialogTitle('')
  }

  useEffect(() => {
    let _regions = []
    let region_names = []
    let service_names = []
    let identity_service_name = null

    const identity_service = services.find(item => item.config_params && item.config_params.is_identity_provider && item.config_params.is_identity_provider === true)
    if (identity_service && identity_service.config_params && identity_service.config_params.default_region) {
      region_names.push(identity_service.config_params.default_region)
    }

    services.forEach(item => {
      if (item.config_params && item.config_params.is_identity_provider === false && !region_names.includes(item.config_params.region_name)) {
        region_names.push(item.config_params.region_name)
      }

      if (!service_names.includes(item.service)) {
        service_names.push(item.service)
      }

      if (item.config_params && item.config_params.is_identity_provider) {
        identity_service_name = item.service
      }
    })

    region_names.forEach((item, index) => {
      _regions.push({
        is_default: index === 0,
        region_name: item,
      })
    })

    setRegions(_regions)
    setServiceNames(service_names)
    setIdentityServiceName(identity_service_name)
  }, [services, showAllRegions])

  const getRegionItem = useCallback((thisIndex, thisItem, sx) => {
    return (
      <RegionItem
        key={thisIndex}
        {...thisItem}
        subscriptionName={subscriptionName}
        serviceNames={serviceNames}
        identityServiceName={identityServiceName}
        services={services}                  
        setRegions={setRegions}
        sx={sx ? sx : null}
        handleServiceEditDialogOpen={handleServiceEditDialogOpen}
        handleRegionDeleteDialogOpen={handleRegionDeleteDialogOpen}    
      />
    )    
  }, [identityServiceName, serviceNames, services, subscriptionName])

  useEffect(() => {
    if (regions.length > 0) {
      let slider_data = []

      regions.forEach((item, index) => {
        slider_data.push(getRegionItem(index, item))
      })

      setSliderData(slider_data)

      let slides_to_show = 3

      if (width < constants.lgBreakpoint) {
        slides_to_show = 1
      } else if (width < constants.xlBreakpoint) {
        slides_to_show = 2
      } else {
        slides_to_show = 3
      }

      setsSlidesToShow(slides_to_show)
    } else {
      setSliderData([])
      setsSlidesToShow(0)
    }
  }, [regions, getRegionItem, width])
  
  return (
    <>
      <Stack
        direction='row'
        alignItems='center'
        justifyContent='flex-start'
        spacing={3}
        sx={{
          padding: '20px 0px 40px 20px',
        }}
      >
        <Box sx={labelStyle}>{defaultTexts.configuration}</Box>
        <ArrowForwardIosIcon fontSize='small' sx={labelArrowStyle} />
        <Box sx={labelStyle}>{defaultTexts.openStackServices}</Box>
      </Stack>

      <Stack
        direction='row'
        alignItems='flex-start'
        justifyContent='flex-start'
      >

        <Box sx={{ width: `${serviceTitleWidth}px` }}>
          <Box sx={allRegionsButtonWrapperStyle}>
            <Button
              variant='text'
              disableRipple
              disableElevation
              onClick={handleShowAllRegions}
              sx={allRegionsButtonStyle}
            >
              {defaultTexts.showAllRegions}
            </Button>
          </Box>

          {
            serviceNames.map((item, index) => (
              <ServiceTitleItem
                key={index}
                service_name={item}
                services={services}
                handleServiceAddDialogOpen={handleServiceAddDialogOpen}
              />
            ))
          }
        </Box>

        <Box sx={{ width: `calc(100% - ${serviceTitleWidth + nextArrowWidth}px)`, marginTop: '-24px' }}>
          {
            (sliderData.length > sliderSlidesToShow) ?
            <CustomSlickSlider
              slides={sliderData}
              arrows={sliderArrows}
              slidesToShow={sliderSlidesToShow}
              draggable={true}     
              centerMode={width < constants.mdBreakpoint ? true : false}
              sx={{ margin: width >= constants.mdBreakpoint ? '0px 50px 0px 0px' : '0px' }}
            /> :
            <>
              {
                regions.map((item, index) => (
                  getRegionItem(index, item, {
                    width: `calc(100%/${sliderSlidesToShow} - 24px)`,
                    float: 'left',
                  })
                ))
              }
            </>
          }
        </Box>

      </Stack>

      <CustomDialog
        open={serviceAddDialogOpen}
        onClose={handleServiceAddDialogClose}
        dialogTitle={{
          title: dialogTitle,
          sx: {
            color: theme.palette.violetsAreBlue,
            fontSize: '22px',
            fontWeight: '500',
          }
        }}
        closeIconColor={theme.palette.violetsAreBlue}
      >
        <ServiceAdd
          subscriptionName={subscriptionName}
          selectedService={selectedService}
          services={services}
          supportedServiceConfigs={supportedServiceConfigs}
          showBillingService={showBillingService}
          setFetchServices={setFetchServices}
          handleServiceAddDialogClose={handleServiceAddDialogClose}
        />
      </CustomDialog>

      <CustomDialog
        open={serviceEditDialogOpen}
        onClose={handleServiceEditDialogClose}
        dialogTitle={{
          title: dialogTitle,
          sx: {
            color: theme.palette.violetsAreBlue,
            fontSize: '22px',
            fontWeight: '500',
          }
        }}
        closeIconColor={theme.palette.violetsAreBlue}
      >
        <ServiceEdit
          subscriptionName={subscriptionName}
          selectedService={selectedService}
          services={services}
          billingServices={billingServices}
          supportedServiceConfigs={supportedServiceConfigs}
          showBillingService={showBillingService}
          setFetchServices={setFetchServices}
          handleServiceEditDialogClose={handleServiceEditDialogClose}
        />
      </CustomDialog>

      <CustomDialog
        open={regionDeleteDialogOpen}
        onClose={handleRegionDeleteDialogClose}
        dialogTitle={{
          title: dialogTitle,
          sx: {
            color: theme.palette.violetsAreBlue,
            fontSize: '22px',
            fontWeight: '500',
          }
        }}
        closeIconColor={theme.palette.violetsAreBlue}
      >
        <RegionDelete
          regionName={selectedService}
          services={services}
          billingServices={billingServices}
          showBillingService={showBillingService}
          setFetchServices={setFetchServices}
          handleRegionDeleteDialogClose={handleRegionDeleteDialogClose}
        />
      </CustomDialog>
    </>
  )
}

export default OpenStackServicesWrapper